import { AutoValidationContext, RoadmapStepValidation } from '../types/roadmap';
import { ROADMAP_WORKFLOW } from '../data/roadmapWorkflow';
import { supabase } from '../lib/supabase';

export async function calculateAutoValidations(
  projectId: string,
  context: AutoValidationContext
): Promise<Array<{ phaseKey: string; stepKey: string; isValid: boolean }>> {
  const results: Array<{ phaseKey: string; stepKey: string; isValid: boolean }> = [];

  for (const phase of ROADMAP_WORKFLOW) {
    for (const step of phase.steps) {
      let isValid = false;

      if (step.autoValidationCondition) {
        try {
          isValid = step.autoValidationCondition(context);
        } catch (error) {
          console.error(
            `Error evaluating auto-validation for ${phase.key}.${step.key}:`,
            error
          );
          isValid = false;
        }
      }

      results.push({
        phaseKey: phase.key,
        stepKey: step.key,
        isValid
      });
    }
  }

  return results;
}

export async function syncAutoValidations(projectId: string): Promise<void> {
  const context = await fetchAutoValidationContext(projectId);
  const autoValidationResults = await calculateAutoValidations(projectId, context);

  for (const result of autoValidationResults) {
    const { data: existing } = await supabase
      .from('roadmap_step_validations')
      .select('*')
      .eq('project_id', projectId)
      .eq('phase_key', result.phaseKey)
      .eq('step_key', result.stepKey)
      .maybeSingle();

    if (existing) {
      await supabase
        .from('roadmap_step_validations')
        .update({
          is_auto_validated: result.isValid,
          validation_date: result.isValid ? new Date().toISOString() : existing.validation_date,
          updated_at: new Date().toISOString()
        })
        .eq('id', existing.id);
    } else if (result.isValid) {
      await supabase
        .from('roadmap_step_validations')
        .insert({
          project_id: projectId,
          phase_key: result.phaseKey,
          step_key: result.stepKey,
          is_auto_validated: true,
          is_manually_validated: false,
          validation_date: new Date().toISOString()
        });
    }
  }
}

export async function fetchAutoValidationContext(
  projectId: string
): Promise<AutoValidationContext> {
  const { data: project } = await supabase
    .from('projects')
    .select('id, status, current_status, created_at')
    .eq('id', projectId)
    .single();

  const { data: documents } = await supabase
    .from('documents')
    .select('id, document_type, is_validated, created_at')
    .eq('project_id', projectId);

  const { data: messages } = await supabase
    .from('messages')
    .select('id, created_at, subject')
    .eq('project_id', projectId)
    .order('created_at', { ascending: false });

  const { data: appointments } = await supabase
    .from('appointments')
    .select('id, status, start_date')
    .eq('project_id', projectId)
    .order('start_date', { ascending: false });

  const { data: projectStages } = await supabase
    .from('project_stages')
    .select('status, reached_at')
    .eq('project_id', projectId)
    .order('reached_at', { ascending: false });

  return {
    project: project || {
      id: projectId,
      status: 'A_PREPARER',
      current_status: null,
      created_at: new Date().toISOString()
    },
    documents: documents || [],
    messages: messages || [],
    appointments: appointments || [],
    projectStages: projectStages || []
  };
}

export function isStepValidated(
  validation: RoadmapStepValidation | null | undefined
): boolean {
  if (!validation) return false;
  return validation.is_manually_validated || validation.is_auto_validated;
}

export function getValidationType(
  validation: RoadmapStepValidation | null | undefined
): 'manual' | 'auto' | 'both' | 'none' {
  if (!validation) return 'none';

  const hasManual = validation.is_manually_validated;
  const hasAuto = validation.is_auto_validated;

  if (hasManual && hasAuto) return 'both';
  if (hasManual) return 'manual';
  if (hasAuto) return 'auto';
  return 'none';
}
